import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {ColorPalettesContainer} from '@Components/color-palettes/color-palettes-container';
import {getColorPalettesByTagIds} from '@Libraries/auto-branding-email-maker';
import {redirectUser} from '@Utils/browser.util';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize} from '@Components/text';
import {IconSize} from '@Components/icon-v2/icon.types';
import Divider from '@Components/divider';
import styles from './color-palette-panel.module.scss';
import {canUserAccessBrandKits, openAppropriateUpsellingDialogForBrandKits} from '@Libraries/brands-library';
import type {ColorPaletteInfo, ColorPalettePill} from '@Libraries/color-palettes.library';
import {addPaletteToRecents, fetchAndUpdateRecentPalettes, getAdminColorPalettes, getUserBrandKitColorPalettes} from '@Libraries/color-palettes.library';
import {ColorPaletteSearchSector} from '../color-palette-search-sector';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {ColorPalettePanelProps} from '@Components/color-palettes/color-palette-panel/color-palette-panel.types';
import {setColorPaletteBeingApplied, setSelectedColorPalette} from '@/reducers/color-palettes.reducer';
import {ColorPalettesLoadingSkeleton} from '@Components/color-palettes/color-palettes-loading-skeleton/color-palettes-loading-skeleton';
import {RecentColorPalettes} from '@Components/color-palettes/recent-color-palettes/recent-color-palettes';

const MAXIMUM_VISIBLE_PALETTES_BRANDKIT = 3;

export function ColorPalettePanel({
  onColorPaletteSelect,
  appliedColorPalette,
  colorPaletteAppliedEvent,
  allowShuffle = false,
  className = '',
  showApplyingState = true,
  ...props
}: ColorPalettePanelProps): ReactElement {
  const dispatch = useAppDispatch();

  const adminColorPalettes = useAppSelector((state) => {
    return state.colorPalettes.adminColorPalettes;
  });

  const brandKitPalettes = useAppSelector((state) => {
    return state.colorPalettes.brandKitColorPalettes;
  });

  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [colorPalettesToDisplay, setColorPalettesToDisplay] = useState<ColorPaletteInfo[]>([]);
  const [showBackToTop, setShowBackToTop] = useState<boolean>(false);
  const parentComponentRef = useRef<HTMLDivElement>(null);
  const backToTopComponentRef = useRef<HTMLDivElement>(null);

  const init = (): void => {
    dispatch(setSelectedColorPalette(appliedColorPalette ?? undefined)); // when opened through grapesjs, the onClick handler that changes the
    // applied palette does not re-render this react component, so we need to keep that duplicated state here in the react component, and keep
    // it in sync
  };

  useEffect(() => {
    init();
  }, [appliedColorPalette]);

  const handlePaletteSelect = (newColorPalette: ColorPaletteInfo): void => {
    dispatch(setColorPaletteBeingApplied(newColorPalette));
    dispatch(setSelectedColorPalette(newColorPalette));

    void addColorPaletteToRecents(newColorPalette);
    onColorPaletteSelect(newColorPalette);
  };

  const onColorPaletteDoneApplying = (): void => {
    dispatch(setColorPaletteBeingApplied(undefined));
  };

  const getPillTagIds = (pills: ColorPalettePill[]): number[] => {
    return Array.from(
      new Set(
        pills.reduce((previousValue: number[], currentValue) => {
          return [
            ...previousValue,
            ...currentValue.tags.map((tag) => {
              return tag.id;
            }),
          ];
        }, [])
      )
    );
  };

  const addColorPaletteToRecents = async (colorPalette: ColorPaletteInfo): Promise<void> => {
    if (!brandKitPalettes) {
      return;
    }

    await addPaletteToRecents(colorPalette);
  };

  const brandKitHandler = (): void => {
    if (!canUserAccessBrandKits()) {
      openAppropriateUpsellingDialogForBrandKits();
      return;
    }

    redirectUser(window.PMW.util.site_url('posters/mine#/brands'), false, false);
  };

  const getAddBrandKitButton = (): ReactElement => {
    return (
      <div>
        <div className={styles.brandKitHeading}>
          <div className="flex-row-align-center">
            <Icon icon="icon-brands" className="icon" size={IconSize.SIZE_ICON_20} />
            <Text val={window.i18next.t('pmwjs_brand_kits')} size={TextSize.XSMALL} bold className="spacing-m-l-2" />
          </div>
          {!canUserAccessBrandKits() ? <Icon className={`icon ${styles.premiumIcon}`} icon="icon-crown" size={IconSize.SIZE_ICON_16} /> : ''}
        </div>
        <Divider additionalClasses="spacing-m-t-2" />
        <Button size={Size.SMALL} type={Type.DASHED} onClick={brandKitHandler} customClasses="spacing-m-t-4 _full-width">
          <Icon icon="icon-plus" className={styles.brandKitAddIcon} />
          <Text size={TextSize.XSMALL} val={window.i18next.t('pmwjs_add_a_brand')} className="_text-transform-none" />
        </Button>
      </div>
    );
  };

  const getBrandKitPaletteComponent = (): ReactElement | null => {
    if (!brandKitPalettes) {
      return <ColorPalettesLoadingSkeleton />;
    }

    if (showSearchResults) {
      return null;
    }

    if (brandKitPalettes.length === 0 || !canUserAccessBrandKits()) {
      return getAddBrandKitButton();
    }

    return (
      <ColorPalettesContainer
        onColorPaletteDoneApplying={onColorPaletteDoneApplying}
        colorPaletteAppliedEvent={colorPaletteAppliedEvent}
        allowShuffle={allowShuffle}
        showApplyingState={showApplyingState}
        heading={window.i18next.t('pmwjs_brand_kits')}
        colorPalettes={brandKitPalettes}
        maximumVisiblePalettes={MAXIMUM_VISIBLE_PALETTES_BRANDKIT}
        showPaletteName
        showNameOverlayOnHover={false}
        onColorPaletteSelected={handlePaletteSelect}
      />
    );
  };

  const getNoSearchResultsComponent = (): ReactElement => {
    return (
      <div className={styles.noResultsContainer}>
        <Icon icon="icon-search" size={IconSize.SIZE_ICON_32} className={`${styles.iconContainer} spacing-p-4 spacing-m-t-5`} />
        <Text val={window.i18next.t('pmwjs_sorry_no_results')} size={TextSize.XSMALL} className="spacing-m-4 -text-align-center " />
        <Button
          size={Size.SMALL}
          type={Type.SECONDARY}
          text={window.i18next.t('pmwjs_back_to_styles')}
          textClasses="_text-transform-none"
          onClick={() => {
            setShowSearchResults(false);
          }}
        />
      </div>
    );
  };

  const getAsyncLoadedComponents = (): ReactElement => {
    return (
      <>
        <RecentColorPalettes
          colorPaletteAppliedEvent={colorPaletteAppliedEvent}
          showApplyingState={showApplyingState}
          allowShuffle={allowShuffle}
          handlePaletteSelect={handlePaletteSelect}
          showSearchResults={showSearchResults}
        />

        {getBrandKitPaletteComponent()}
      </>
    );
  };

  const getAdminColorPaletteComponent = (): ReactElement | null => {
    if (showSearchResults) {
      return null;
    }

    if (!adminColorPalettes) {
      return <ColorPalettesLoadingSkeleton />;
    }

    return (
      <ColorPalettesContainer
        onColorPaletteDoneApplying={onColorPaletteDoneApplying}
        colorPaletteAppliedEvent={colorPaletteAppliedEvent}
        allowShuffle={allowShuffle}
        showApplyingState={showApplyingState}
        heading={window.i18next.t('pmwjs_all_styles')}
        colorPalettes={adminColorPalettes}
        onColorPaletteSelected={handlePaletteSelect}
        showNameOverlayOnHover
      />
    );
  };

  const getSearchColorPaletteComponent = (): ReactElement | null => {
    if (!showSearchResults) {
      return null;
    }

    if (colorPalettesToDisplay.length === 0) {
      return getNoSearchResultsComponent();
    }

    return (
      <ColorPalettesContainer
        onColorPaletteDoneApplying={onColorPaletteDoneApplying}
        colorPaletteAppliedEvent={colorPaletteAppliedEvent}
        allowShuffle={allowShuffle}
        showApplyingState={showApplyingState}
        heading={null}
        colorPalettes={colorPalettesToDisplay}
        onColorPaletteSelected={handlePaletteSelect}
        showNameOverlayOnHover
      />
    );
  };

  const handleSelectedPillsChange = (pills: ColorPalettePill[], newlyToggledPill?: ColorPalettePill): void => {
    setShowSearchResults(pills.length > 0);

    const tags = getPillTagIds(pills);
    if (tags.length === 0) {
      setColorPalettesToDisplay([]);
      return;
    }

    scrollPanelToTop();
    setColorPalettesToDisplay(getColorPalettesByTagIds(tags));

    if (newlyToggledPill) {
      props.onPillSelected(newlyToggledPill);
    }
  };

  const getBackToTopButton = (): ReactElement => {
    return (
      <Button
        customClasses={styles.backToTopButton}
        size={Size.XSMALL}
        icon="icon-arrow-top"
        text={window.i18next.t('pmwjs_back_to_top')}
        iconPlacement={IconPlacement.LEFT}
        onClick={() => {
          scrollPanelToTop(true);
        }}
      />
    );
  };

  const getIntersectionObserverForScroll = (): IntersectionObserver => {
    return new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setShowBackToTop(!entry.isIntersecting);
        });
      },
      {threshold: 1}
    );
  };

  const scrollPanelToTop = (smooth = false): void => {
    parentComponentRef.current?.scrollIntoView({block: 'start', behavior: smooth ? 'smooth' : 'instant'});
  };

  useEffect(() => {
    void getAdminColorPalettes();
    void getUserBrandKitColorPalettes();
    void fetchAndUpdateRecentPalettes();

    const observer = getIntersectionObserverForScroll();
    observer.observe(backToTopComponentRef.current as Element);

    return (): void => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className={className} ref={parentComponentRef}>
      <div ref={backToTopComponentRef} />
      <ColorPaletteSearchSector
        onSearchSubmit={(searchTerm: string, colorPalettes: ColorPaletteInfo[]) => {
          setShowSearchResults(searchTerm !== '');
          setColorPalettesToDisplay(colorPalettes);
          if (searchTerm !== '') {
            scrollPanelToTop();
          }

          props.onSearchSubmit(searchTerm);
        }}
        onSearchInput={(searchTerm: string) => {
          if (searchTerm === '') {
            setShowSearchResults(false);
          }
        }}
        onSelectedPillsChange={handleSelectedPillsChange}
        showSearchResults={showSearchResults}
        showShadow={showBackToTop}
        brandKitPalettes={brandKitPalettes}
      />
      <div className={styles.panelComponents}>
        {getAsyncLoadedComponents()}
        {getAdminColorPaletteComponent()}
        {getSearchColorPaletteComponent()}
      </div>
      <div className={`${styles.backToTopButtonContainer} ${!showBackToTop ? '_hidden' : ''}`}>{getBackToTopButton()}</div>
    </div>
  );
}
